import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { leesStartfilter, type Geweigerd, type Lijst, type Startfilter } from '@xxtract/tabelfilters';
import { useLijstStandStore } from '@/stores/lijstStandStore';
import { standUitStart } from './useTabelStand';

/** Markering in `location.state`: dit adres is alleen opgeruimd, geen nieuwe paginaweergave (§7). */
export const STARTADRES_OPGERUIMD = 'startadresOpgeruimd';

/** Een geweigerde linkwaarde, met de kolomkop zodat `FilterRejectedMessage` de kolom kan noemen. */
export interface LinkWeigering extends Geweigerd {
  kopSleutel?: string;
}

const GEEN_START: Record<string, Startfilter | undefined> = {};
const LEGE_START: Startfilter = { filters: {}, sortering: [], pagina: 1, gebruikt: [], geweigerd: [] };

export interface StartadresLijst {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- elke rijvorm; alleen de kolommen tellen
  lijst: Lijst<any, any>;
  standSleutel: string;
  /**
   * Een stand die meerdere schermen delen (de Processen-filter, story 12): een link die geen van zijn
   * parameters noemt laat hem staan; noemt hij er wél een, dan wordt hij precies de link, via
   * `zetPaginafilter` zodat afhankelijke lijsten naar pagina 1 gaan.
   */
  gedeeld?: boolean;
}

/**
 * Eén lezer per pagina (§7): leest het adres, verdeelt de parameters over de lijststanden, en haalt
 * de gelezen parameters met `replace` uit het adres. De stand wordt tijdens de render afgeleid (de
 * eerste vraag is al gefilterd) en in een layout-effect bewaard. Idempotent onder StrictMode.
 */
export function useStartadres(lijsten: readonly StartadresLijst[]) {
  const location = useLocation();
  const navigate = useNavigate();
  const [geweigerd, setGeweigerd] = useState<LinkWeigering[]>([]);
  const verwerkt = useRef<string | null>(null);
  /** De paren (`p=w`) van de laatst toegepaste link; vervallen zodra een adres zonder lijstparameters langskomt. */
  const verwerkteLink = useRef<Set<string> | null>(null);
  const opruimen = useRef<string[] | null>(null);

  // ponytail: herberekent alleen bij een ander adres; `lijsten` is per pagina vast.
  const gelezen = useMemo(() => {
    const params = new URLSearchParams(location.search);
    // Gedeelde standen eerst: hun `zetPaginafilter` zet afhankelijke lijsten op 1 vóór die hun eigen linkpagina krijgen.
    return [...lijsten]
      .sort((a, b) => Number(Boolean(b.gedeeld)) - Number(Boolean(a.gedeeld)))
      .map(({ lijst, standSleutel, gedeeld }) => ({ lijst, standSleutel, gedeeld, start: leesStartfilter(params, lijst) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Precies de link (§7): raakt de link één lijst van de pagina, dan beginnen de andere bij hun standaard.
  const start = useMemo(() => {
    const uit: Record<string, Startfilter | undefined> = {};
    if (!gelezen.some((g) => g.start.gebruikt.length > 0)) return uit;
    for (const g of gelezen) {
      if (g.start.gebruikt.length > 0) uit[g.standSleutel] = g.start;
      else if (!g.gedeeld) uit[g.standSleutel] = LEGE_START;
    }
    return uit;
  }, [gelezen]);

  const link = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return [...new Set(gelezen.flatMap((g) => g.start.gebruikt))].map((p) => `${p}=${params.get(p)}`);
  }, [gelezen, location.search]);
  // Een ander adres met (een deel van) dezelfde, nog niet opgeruimde link — een paginafilter die het adres
  // wijzigt, zoals de Processen-filter — is al toegepast en mag de lijststand niet opnieuw overschrijven.
  const alInLink = link.length > 0 && link.every((paar) => verwerkteLink.current?.has(paar));

  useLayoutEffect(() => {
    const gebruikt = new Set(gelezen.flatMap((g) => g.start.gebruikt));
    if (verwerkt.current === location.key) return;
    if (gebruikt.size === 0) verwerkteLink.current = null;
    if (alInLink) {
      verwerkt.current = location.key;
      return;
    }
    if (gebruikt.size === 0) {
      // Later naar een adres zonder lijstparameters: een oude melding hoort daar niet meer bij.
      const opgeruimd = (location.state as Record<string, unknown> | null)?.[STARTADRES_OPGERUIMD] === true;
      if (!opgeruimd) setGeweigerd((oud) => (oud.length > 0 ? [] : oud));
      return;
    }
    verwerkt.current = location.key;
    verwerkteLink.current = new Set(link);

    const { zet, zetPaginafilter } = useLijstStandStore.getState();
    const weigeringen: LinkWeigering[] = [];
    for (const { lijst, standSleutel, gedeeld, start: s } of gelezen) {
      if (gedeeld) {
        if (s.gebruikt.length > 0) zetPaginafilter(standSleutel, s.filters);
      } else {
        zet(standSleutel, standUitStart(s.gebruikt.length > 0 ? s : LEGE_START));
      }
      for (const w of s.geweigerd) {
        // Zonder kolom (sort, sortBy) noemt de melding de sortering, niet de kale parameternaam.
        const kop = w.sleutel
          ? lijst.kolommen.find((k) => k.sleutel === w.sleutel)?.kopSleutel
          : /sort(By)?$/.test(w.parameter)
            ? 'tabel.sorteerOp'
            : undefined;
        weigeringen.push(kop ? { ...w, kopSleutel: kop } : w);
      }
    }
    setGeweigerd(weigeringen);
    opruimen.current = [...gebruikt];
  }, [gelezen, link, alInLink, location.key, location.state]);

  // Het adres opruimen in een gewoon effect: in een layout-effect van de eerste render luistert de
  // router nog niet (ouder-effecten lopen na die van het kind) en gaat de `replace` verloren.
  useEffect(() => {
    const gebruikt = opruimen.current;
    if (!gebruikt) return;
    opruimen.current = null;
    const rest = new URLSearchParams(location.search);
    for (const p of gebruikt) rest.delete(p);
    const zoek = rest.toString();
    navigate(
      { pathname: location.pathname, search: zoek ? `?${zoek}` : '', hash: location.hash },
      { replace: true, state: { ...(location.state as Record<string, unknown> | null), [STARTADRES_OPGERUIMD]: true } },
    );
  }, [location, navigate]);

  const wisGeweigerd = useCallback(() => setGeweigerd([]), []);
  // Eenmaal bewaard is de lijststand de bron: een adres dat (nog) niet is opgeruimd mag een latere
  // wijziging (reset, "Filters wissen") niet overschrijven.
  const alToegepast = verwerkt.current === location.key || alInLink;
  return { start: alToegepast ? GEEN_START : start, geweigerd, wisGeweigerd };
}

